import { CONFIG, PORTAL_NAME } from 'constants/index';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { AppService } from 'services';
import { useTheme } from 'styled-components';
import { formatLabelButton, mergeObject } from 'utils/function';
import {
  GenerateDataTranslation,
  replaceVarsInTranslation,
} from 'utils/translation';

type PclKey =
  | 'pcOrder'
  | 'pcReturn'
  | 'pcRefresh'
  | 'pcRegisterDevice'
  | 'pcReturnAnyDevice'
  | 'pcLostStolen'
  | 'pcReportBroken'
  | 'assignmentIssue';

interface Options {
  key: PclKey;
  variables: Record<string, string>;
}

const descriptionConfig: Record<PclKey, { KEY: string; LABEL: string }> = {
  pcOrder: CONFIG.PC_ORDER_PAGE_DESCRIPTIONS,
  pcReturn: CONFIG.PC_RETURN_PAGE_DESCRIPTIONS,
  pcRefresh: CONFIG.PC_REFRESH_PAGE_DESCRIPTIONS,
  pcRegisterDevice: CONFIG.PC_REGISTER_DEVICE_PAGE_DESCRIPTIONS,
  pcReturnAnyDevice: CONFIG.PC_RETURN_UNKNOWN_DEVICE_PAGE_DESCRIPTIONS,
  pcLostStolen: CONFIG.PC_LOST_STOLEN_PAGE_DESCRIPTIONS,
  pcReportBroken: CONFIG.PC_REPORT_BROKEN_PAGE_DESCRIPTIONS,
  assignmentIssue: CONFIG.PC_DONT_HAVE_DEVICE_PAGE_DESCRIPTIONS,
};

export const usePclDescriptions = ({ key, variables: initVariables }: Options) => {
  const theme = useTheme();

  const { t } = useTranslation(['common']);
  const pclTranslation = GenerateDataTranslation(key);

  const [descriptions, setDescriptions] = useState(pclTranslation);
  const [configDescriptions, setConfigDescriptions] = useState({});

  const { generalConfig } = useSelector(
    (state: RootState) => state.configReducer,
  );
  const { verbiageConfig, portalName } = generalConfig;

  const transformButton = (label = '') => {
    return formatLabelButton(label, theme.uptimeTextTransform.button);
  };

  const variablesJson = JSON.stringify({
    ...initVariables,
    start_button_label: transformButton(descriptions?.startButtonLabel),
    next_button_label: transformButton(descriptions?.nextButtonLabel),
    back_button_label: transformButton(descriptions?.backButtonLabel),
    submit_button_label: transformButton(descriptions?.submitButtonLabel),
    continue_button_label: transformButton(descriptions?.continueButtonLabel),
    yes_button_label: transformButton(descriptions?.confirmDevice?.button?.yes),
    no_button_label: transformButton(descriptions?.confirmDevice?.button?.no),
    chat_button_label: transformButton(descriptions?.chatButtonLabel),
    end_chat_text: descriptions?.endChatText,
    home_text: t('common:homeText'),
    portalName: portalName || PORTAL_NAME,
  });

  useEffect(() => {
    if (!verbiageConfig) return;

    (async () => {
      try {
        const res = await AppService.getValueConfigAzure(
          descriptionConfig[key].KEY,
          descriptionConfig[key].LABEL,
        );
        const newDescriptions = res || {};
        setConfigDescriptions(newDescriptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [verbiageConfig]);

  useEffect(() => {
    if (isEmpty(pclTranslation)) return;
    const variables = JSON.parse(variablesJson);
    const mergedDescriptions = mergeObject(pclTranslation, configDescriptions);
    const replacedDescriptions = replaceVarsInTranslation(mergedDescriptions, variables);
    setDescriptions(replacedDescriptions);
  }, [pclTranslation, configDescriptions, variablesJson]);

  return descriptions;
};
